import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle, ModernButton, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Input } from '../ui'
import type { EntityDocument } from '../documents/DocumentList'
import { DocumentUpload } from '../documents'
import type { SaleOnboardingProgress } from '../../types'
import { apiService } from '../../services/api'

interface SaleOnboardingRequirementsCardProps {
 saleId: string
 customerId?: string | null
 progress: SaleOnboardingProgress | null
 saleDocuments: EntityDocument[]
 customerDocuments: EntityDocument[]
 canEdit: boolean
 onUpdated: () => void
 onToast: (message: string, type: 'success' | 'error') => void
 omitContracts?: boolean
}

export const SaleOnboardingRequirementsCard: React.FC<SaleOnboardingRequirementsCardProps> = ({
 saleId,
 customerId,
 progress,
 saleDocuments,
 customerDocuments,
 canEdit,
 onUpdated,
 onToast,
 omitContracts = false,
}) => {
 const { t } = useTranslation()
 const [linkingId, setLinkingId] = useState<string | null>(null)
 const [selectedDocByFulfillment, setSelectedDocByFulfillment] = useState<Record<string, string>>({})
 const [contractDateByFulfillment, setContractDateByFulfillment] = useState<Record<string, string>>({})
 const [contractDocByFulfillment, setContractDocByFulfillment] = useState<Record<string, string>>({})
 const [signingId, setSigningId] = useState<string | null>(null)

 if (!progress) {
 return null
 }

 const pendingDocs = progress.documentation.customerDocuments.filter((d) => d.isRequired && !d.entityDocumentId)
 const pendingContracts = progress.documentation.contracts.filter((c) => c.isRequired && !c.signedAt)
 const visiblePendingContracts = omitContracts ? [] : pendingContracts

 if (pendingDocs.length === 0 && visiblePendingContracts.length === 0) {
 return null
 }

 const docsForRow = (attachTo: string) => (attachTo === 'customer' ? customerDocuments : saleDocuments)

 const linkCustomerDoc = async (fulfillmentId: string) => {
 const entityDocumentId = selectedDocByFulfillment[fulfillmentId]
 if (!entityDocumentId) {
 onToast(t('financialSales.saleOnboarding.pickDocumentFirst'), 'error')
 return
 }
 try {
 setLinkingId(fulfillmentId)
 await apiService.patchSaleCustomerDocFulfillment(saleId, fulfillmentId, entityDocumentId)
 onToast(t('financialSales.saleOnboarding.docLinked'), 'success')
 onUpdated()
 } catch (err: unknown) {
 const msg = err instanceof Error ? err.message : t('financialSales.saleOnboarding.docLinkError')
 onToast(msg, 'error')
 } finally {
 setLinkingId(null)
 }
 }

 const signContract = async (fulfillmentId: string) => {
 const dateVal = contractDateByFulfillment[fulfillmentId]
 if (!dateVal) {
 onToast(t('financialSales.saleOnboarding.pickSignDate'), 'error')
 return
 }
 const signedAt = new Date(dateVal).toISOString()
 const rawDoc = contractDocByFulfillment[fulfillmentId]
 const entityDocumentId = rawDoc && rawDoc.length > 0 ? rawDoc : undefined
 try {
 setSigningId(fulfillmentId)
 await apiService.patchSaleContractFulfillment(saleId, fulfillmentId, {
 signedAt,
 ...(entityDocumentId ? { entityDocumentId } : {}),
 })
 onToast(t('financialSales.saleOnboarding.contractSigned'), 'success')
 onUpdated()
 } catch (err: unknown) {
 const msg = err instanceof Error ? err.message : t('financialSales.saleOnboarding.contractSignError')
 onToast(msg, 'error')
 } finally {
 setSigningId(null)
 }
 }

 return (
 <Card className="border-dashed border-accent/40">
 <CardHeader className="pb-3">
 <CardTitle className="text-base">{t('financialSales.saleOnboarding.requirementsTitle')}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 {pendingDocs.length > 0 && (
 <div className="space-y-3">
 <p className="text-sm font-medium text-foreground">{t('financialSales.saleOnboarding.customerDocsSection')}</p>
 {pendingDocs.some((r) => r.attachTo === 'customer') && customerId && canEdit && (
 <Card className="bg-muted/30 border-border">
 <CardHeader className="py-3">
 <CardTitle className="text-sm">{t('financialSales.saleOnboarding.uploadCustomerTitle')}</CardTitle>
 </CardHeader>
 <CardContent>
 <DocumentUpload
 entityType="customer"
 entityId={customerId}
 saleId={saleId}
 onUploadSuccess={() => void onUpdated()}
 />
 </CardContent>
 </Card>
 )}
 {pendingDocs.map((row) => {
 const pool = docsForRow(row.attachTo)
 return (
 <div
 key={row.fulfillmentId}
 className="flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-end sm:flex-wrap"
 >
 <div className="flex-1 min-w-[160px]">
 <p className="text-sm font-medium">{row.name}</p>
 <p className="text-xs text-muted-foreground">
 {row.attachTo === 'customer'
 ? t('financialSales.saleOnboarding.attachCustomer')
 : t('financialSales.saleOnboarding.attachSale')}
 </p>
 </div>
 <div className="flex flex-col gap-2 sm:flex-row sm:items-center flex-1 min-w-[200px]">
 <Select
 value={selectedDocByFulfillment[row.fulfillmentId] || ''}
 onValueChange={(v) =>
 setSelectedDocByFulfillment((prev) => ({ ...prev, [row.fulfillmentId]: v }))
 }
 disabled={!canEdit || pool.length === 0}
 >
 <SelectTrigger className="w-full sm:min-w-[200px]">
 <SelectValue placeholder={t('financialSales.saleOnboarding.selectDocument')} />
 </SelectTrigger>
 <SelectContent>
 {pool.map((d) => (
 <SelectItem key={d.id} value={d.id}>
 {d.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 <ModernButton
 type="button"
 size="sm"
 disabled={!canEdit || linkingId === row.fulfillmentId || pool.length === 0}
 onClick={() => void linkCustomerDoc(row.fulfillmentId)}
 >
 {linkingId === row.fulfillmentId
 ? t('financialSales.saleOnboarding.linking')
 : t('financialSales.saleOnboarding.linkDocument')}
 </ModernButton>
 </div>
 {pool.length === 0 && (
 <p className="text-xs text-amber-600 dark:text-amber-400 w-full">
 {t('financialSales.saleOnboarding.uploadFirstHint')}
 </p>
 )}
 </div>
 )
 })}
 </div>
 )}

 {visiblePendingContracts.length > 0 && (
 <div className="space-y-3">
 <p className="text-sm font-medium text-foreground">{t('financialSales.saleOnboarding.contractsSection')}</p>
 {visiblePendingContracts.map((row) => (
 <div key={row.fulfillmentId} className="rounded-lg border border-border p-3 space-y-3">
 <p className="text-sm font-medium">{row.name}</p>
 {row.referenceFileUrl && (
 <a
 href={row.referenceFileUrl}
 target="_blank"
 rel="noopener noreferrer"
 className="text-xs text-accent hover:underline"
 >
 {t('financialSales.saleOnboarding.openTemplate')}
 </a>
 )}
 <div className="grid gap-3 sm:grid-cols-2">
 <div>
 <Label className="text-xs">{t('financialSales.saleOnboarding.signDate')}</Label>
 <Input
 type="date"
 className="mt-1"
 value={contractDateByFulfillment[row.fulfillmentId] || ''}
 onChange={(e) =>
 setContractDateByFulfillment((prev) => ({
 ...prev,
 [row.fulfillmentId]: e.target.value,
 }))
 }
 disabled={!canEdit}
 />
 </div>
 <div>
 <Label className="text-xs">{t('financialSales.saleOnboarding.signedPdfOptional')}</Label>
 <Select
 value={contractDocByFulfillment[row.fulfillmentId] || '__none__'}
 onValueChange={(v) =>
 setContractDocByFulfillment((prev) => ({
 ...prev,
 [row.fulfillmentId]: v === '__none__' ? '' : v,
 }))
 }
 disabled={!canEdit}
 >
 <SelectTrigger className="mt-1">
 <SelectValue placeholder={t('financialSales.saleOnboarding.optional')} />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="__none__">{t('financialSales.saleOnboarding.none')}</SelectItem>
 {saleDocuments.map((d) => (
 <SelectItem key={d.id} value={d.id}>
 {d.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 <ModernButton
 type="button"
 size="sm"
 disabled={!canEdit || signingId === row.fulfillmentId}
 onClick={() => void signContract(row.fulfillmentId)}
 >
 {signingId === row.fulfillmentId
 ? t('financialSales.saleOnboarding.saving')
 : t('financialSales.saleOnboarding.confirmSignature')}
 </ModernButton>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 )
}
